import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../utils/axios';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import AddonGroupsDebug from '../components/AddonGroupsDebug';
import FileUpload from '../components/FileUpload';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Package,
  Tag,
  Eye,
  EyeOff,
  Grid,
  List,
  MoreVertical,
  DollarSign,
  Star,
  Clock,
  Hash,
  X,
  Settings,
  Layers,
  Users,
  Link,
  Unlink,
  Group,
  FolderOpen,
  FolderPlus,
} from 'lucide-react';

const AddonGroups = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMenu, setSelectedMenu] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [filterRequired, setFilterRequired] = useState('all'); // all, required, optional
  const [sortBy, setSortBy] = useState('name'); // name, created_at, sort_order
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showGroupDetailsModal, setShowGroupDetailsModal] = useState(false);
  const [selectedGroupForDetails, setSelectedGroupForDetails] = useState(null);
  const [showAddAddonModal, setShowAddAddonModal] = useState(false);
  const [showEditAddonModal, setShowEditAddonModal] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const queryClient = useQueryClient();

  // Prevent body scroll when modals are open
  useEffect(() => {
    const isModalOpen = showCreateModal || showMappingModal || showGroupDetailsModal || showAddAddonModal || showEditAddonModal;
    
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showCreateModal, showMappingModal, showGroupDetailsModal, showAddAddonModal, showEditAddonModal]);



  const { data: menuItems, isLoading: menuItemsLoading } = useQuery(
    ['menu-items', selectedMenu, selectedCategory, sortBy],
    async () => {
      const params = {};
      if (selectedMenu) params.menu_id = selectedMenu;
      if (selectedCategory) params.category_id = selectedCategory;
      if (sortBy) params.sort_by = sortBy;
      const response = await api.get('/vendor/menu-items', { params });
      return Array.isArray(response.data) ? response.data : [];
    }
  );

  // Enhanced vendor addon groups query with usage stats
  const { data: addonGroups, isLoading: addonGroupsLoading } = useQuery(
    ['vendor-addon-groups'],
    async () => {
      const response = await api.get('/api/vendor/addon-groups');
      return response.data.addonGroups || [];
    }
  );

  // Query for menu items available for mapping
  const { data: mappableMenuItems } = useQuery(
    ['vendor-menu-items-for-mapping'],
    async () => {
      const response = await api.get('/api/vendor/menu-items-for-mapping');
      return response.data.menuItems || [];
    }
  );

  const { data: menuItemAddonGroups, isLoading: menuItemAddonGroupsLoading } = useQuery(
    ['menu-item-addon-groups', selectedMenuItem?.id],
    async () => {
      if (!selectedMenuItem) return [];
      const response = await api.get(`/api/menu-items/${selectedMenuItem.id}/addon-groups`);
      return response.data.addonGroups || [];
    },
    { enabled: !!selectedMenuItem }
  );

  const { data: menus } = useQuery(
    ['menus'],
    async () => {
      const response = await api.get('/vendor/menus');
      return Array.isArray(response.data) ? response.data : [];
    }
  );

  const { data: categories } = useQuery(
    ['menu-categories', selectedMenu],
    async () => {
      if (!selectedMenu) return [];
      const response = await api.get(`/vendor/menus/${selectedMenu}/categories`);
      return Array.isArray(response.data) ? response.data : [];
    },
    { enabled: !!selectedMenu }
  );

  // Query for detailed group information when viewing group details
  const { data: detailedGroupData, isLoading: detailedGroupLoading } = useQuery(
    ['addon-group-details', selectedGroupForDetails?.id],
    async () => {
      if (!selectedGroupForDetails?.id) return null;
      const response = await api.get(`/api/vendor/addon-groups/${selectedGroupForDetails.id}/details`);
      return response.data.addonGroup || null;
    },
    { enabled: !!selectedGroupForDetails?.id }
  );

  const createAddonGroupMutation = useMutation(
    async (groupData) => {
      const response = await api.post('/api/addon-groups', groupData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vendor-addon-groups']);
        queryClient.invalidateQueries(['addon-groups']);
        showSuccessToast('Addon group created successfully!', 'addon-group-create');
        setShowCreateModal(false);
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to create addon group', 'addon-group-create');
      },
    }
  );

  // Enhanced mutation for creating addons within groups
  const createAddonInGroupMutation = useMutation(
    async ({ groupId, addonData }) => {
      const response = await api.post(`/api/vendor/addon-groups/${groupId}/addons`, addonData);
      return response.data;
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['vendor-addon-groups']);
        queryClient.invalidateQueries(['addon-group-details', variables.groupId]);
        showSuccessToast('Addon added to group successfully!', 'addon-create');
        setShowAddAddonModal(false);
      },
      onError: (error) => {
        console.error('Add addon error:', error);
        let errorMessage = 'Failed to add addon to group';
        
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
          
          // Handle specific database schema error
          if (errorMessage.includes('menu_item_id') && errorMessage.includes('null')) {
            errorMessage = '🔧 Database needs updating! Please run: ALTER TABLE menu_item_addons ALTER COLUMN menu_item_id DROP NOT NULL; Then restart server.';
          }
        }
        
        showErrorToast(errorMessage, 'addon-create');
      },
    }
  );

  const updateAddonGroupMutation = useMutation(
    async ({ groupId, data }) => {
      const response = await api.put(`/api/addon-groups/${groupId}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['addon-groups']);
        queryClient.invalidateQueries(['menu-item-addon-groups']);
        showSuccessToast('Addon group updated successfully!', 'addon-group-update');
        setShowCreateModal(false);
        setEditingGroup(null);
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to update addon group', 'addon-group-update');
      },
    }
  );

  const deleteAddonGroupMutation = useMutation(
    async (groupId) => {
      const response = await api.delete(`/api/addon-groups/${groupId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['addon-groups']);
        queryClient.invalidateQueries(['menu-item-addon-groups']);
        showSuccessToast('Addon group deleted successfully!', 'addon-group-delete');
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to delete addon group', 'addon-group-delete');
      },
    }
  );

  const mapAddonGroupMutation = useMutation(
    async ({ itemId, groupId, sortOrder }) => {
      const response = await api.post(`/api/menu-items/${itemId}/addon-groups/${groupId}`, {
        sort_order: sortOrder
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['menu-item-addon-groups']);
        showSuccessToast('Addon group mapped successfully!', 'addon-group-map');
        setShowMappingModal(false);
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to map addon group', 'addon-group-map');
      },
    }
  );

  const unmapAddonGroupMutation = useMutation(
    async ({ itemId, groupId }) => {
      const response = await api.delete(`/api/menu-items/${itemId}/addon-groups/${groupId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['menu-item-addon-groups']);
        showSuccessToast('Addon group unmapped successfully!', 'addon-group-unmap');
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to unmap addon group', 'addon-group-unmap');
      },
    }
  );

  // Mutation for updating addons within groups
  const updateAddonInGroupMutation = useMutation(
    async ({ groupId, addonId, addonData }) => {
      const response = await api.put(`/api/vendor/addon-groups/${groupId}/addons/${addonId}`, addonData);
      return response.data;
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['vendor-addon-groups']);
        queryClient.invalidateQueries(['addon-group-details', variables.groupId]);
        showSuccessToast('Addon updated successfully!', 'addon-update');
        setShowEditAddonModal(false);
        setEditingAddon(null);
        setRemoveImage(false);
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to update addon', 'addon-update');
      },
    }
  );

  // Mutation for deleting addons from groups
  const deleteAddonFromGroupMutation = useMutation(
    async ({ groupId, addonId }) => {
      const response = await api.delete(`/api/vendor/addon-groups/${groupId}/addons/${addonId}`);
      return response.data;
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['vendor-addon-groups']);
        queryClient.invalidateQueries(['addon-group-details', variables.groupId]);
        showSuccessToast('Addon deleted successfully!', 'addon-delete');
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to delete addon', 'addon-delete');
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      description: formData.get('description'),
      is_required: formData.get('is_required') === 'true',
      min_quantity: parseInt(formData.get('min_quantity')),
      max_quantity: formData.get('max_quantity') ? parseInt(formData.get('max_quantity')) : null,
      sort_order: parseInt(formData.get('sort_order'))
    };

    if (editingGroup) {
      updateAddonGroupMutation.mutate({ groupId: editingGroup.id, data });
    } else {
      createAddonGroupMutation.mutate(data);
    }
  };

  const handleDelete = (groupId) => {
    if (window.confirm('Are you sure you want to delete this addon group?')) {
      deleteAddonGroupMutation.mutate(groupId);
    }
  };

  const handleMapGroup = (groupId) => {
    setSelectedGroup(addonGroups.find(group => group.id === groupId));
    setShowMappingModal(true);
  };

  const handleUnmapGroup = (groupId) => {
    if (window.confirm('Are you sure you want to unmap this addon group?')) {
      unmapAddonGroupMutation.mutate({
        itemId: selectedMenuItem.id,
        groupId: groupId
      });
    }
  };

  const handleViewGroupDetails = (group) => {
    setSelectedGroupForDetails(group);
    setShowGroupDetailsModal(true);
  };

  const handleAddAddonToGroup = (group) => {
    setSelectedGroupForDetails(group);
    setShowAddAddonModal(true);
  };

  const handleSubmitAddonToGroup = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const addonData = {
      name: formData.get('name'),
      description: formData.get('description'),
      price: parseFloat(formData.get('price')),
      max_quantity: parseInt(formData.get('max_quantity')) || 1,
      min_quantity: parseInt(formData.get('min_quantity')) || 0,
      is_available: formData.get('is_available') === 'true',
      sort_order: parseInt(formData.get('sort_order')) || 0,
      default: formData.get('default') === 'true',
      image_url: formData.get('image_url') || null
    };

    createAddonInGroupMutation.mutate({
      groupId: selectedGroupForDetails.id,
      addonData
    });
  };

  const handleMapSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const sortOrder = parseInt(formData.get('sort_order')) || 0;

    mapAddonGroupMutation.mutate({
      itemId: selectedMenuItem.id,
      groupId: selectedGroup.id,
      sortOrder: sortOrder
    });
  };

  const handleEditAddon = (addon) => {
    setEditingAddon(addon);
    setRemoveImage(false);
    setShowEditAddonModal(true);
  };

  const handleDeleteAddon = (addon) => {
    if (window.confirm(`Are you sure you want to delete "${addon.name}"?`)) {
      deleteAddonFromGroupMutation.mutate({
        groupId: selectedGroupForDetails.id,
        addonId: addon.id
      });
    }
  };

  const handleSubmitEditAddon = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newImageUrl = formData.get('image_url');
    
    const addonData = {
      name: formData.get('name'),
      description: formData.get('description'),
      price: parseFloat(formData.get('price')),
      max_quantity: parseInt(formData.get('max_quantity')) || 1,
      min_quantity: parseInt(formData.get('min_quantity')) || 0,
      is_available: formData.get('is_available') === 'true',
      sort_order: parseInt(formData.get('sort_order')) || 0,
      default: formData.get('default') === 'true',
      // Handle image_url logic:
      // - If removeImage is true: set to null
      // - If new image uploaded: use new URL
      // - If no change: preserve existing image_url
      image_url: removeImage ? null : (newImageUrl || editingAddon.image_url || null)
    };

    updateAddonInGroupMutation.mutate({
      groupId: selectedGroupForDetails.id,
      addonId: editingAddon.id,
      addonData
    });
  };

  const filteredAddonGroups = addonGroups?.filter(group => {
    const matchesSearch = group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         group.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterRequired === 'all' ||
                         (filterRequired === 'required' && group.is_required) ||
                         (filterRequired === 'optional' && !group.is_required);
    return matchesSearch && matchesFilter;
  }) || [];

  const AddonGroupModal = ({ group = null, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-hidden">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            {group ? 'Edit Addon Group' : 'Create Addon Group'}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              name="name"
              defaultValue={group?.name || ''}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              defaultValue={group?.description || ''}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Quantity
              </label>
              <input
                type="number"
                name="min_quantity"
                min="0"
                defaultValue={group?.min_quantity || 0}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Quantity
              </label>
              <input
                type="number"
                name="max_quantity"
                min="1"
                defaultValue={group?.max_quantity || ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort Order
              </label>
              <input
                type="number"
                name="sort_order"
                defaultValue={group?.sort_order || 0}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Required
              </label>
              <select
                name="is_required"
                defaultValue={group?.is_required?.toString() || 'false'}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="false">Optional</option>
                <option value="true">Required</option>
              </select>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              disabled={createAddonGroupMutation.isLoading || updateAddonGroupMutation.isLoading}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {createAddonGroupMutation.isLoading || updateAddonGroupMutation.isLoading
                ? 'Saving...'
                : group ? 'Update Group' : 'Create Group'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const MappingModal = ({ onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-hidden">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            Map Addon Group to Menu Item
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">
            <strong>Menu Item:</strong> {selectedMenuItem?.name}
          </p>
          <p className="text-sm text-gray-600">
            <strong>Addon Group:</strong> {selectedGroup?.name}
          </p>
        </div>

        <form onSubmit={handleMapSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort Order
            </label>
            <input
              type="number"
              name="sort_order"
              defaultValue="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              disabled={mapAddonGroupMutation.isLoading}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {mapAddonGroupMutation.isLoading ? 'Mapping...' : 'Map Group'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      {/* Debug Toggle */}
      {/* <div className="mb-4">
        <button
          onClick={() => setShowDebug(!showDebug)}
          className="text-sm bg-yellow-100 text-yellow-800 px-3 py-1 rounded border border-yellow-300 hover:bg-yellow-200"
        >
          {showDebug ? '🔍 Hide Debug' : '🧪 Show API Debug'}
        </button>
      </div> */}

      {/* Debug Panel */}
      {showDebug && (
        <div className="mb-6 border-2 border-yellow-300 rounded-lg">
          <AddonGroupsDebug />
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Addon Groups</h1>
          <p className="text-gray-600">Manage addon groups and their mappings to menu items</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Create Group</span>
        </button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search groups..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter</label>
            <select
              value={filterRequired}
              onChange={(e) => setFilterRequired(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Groups</option>
              <option value="required">Required Only</option>
              <option value="optional">Optional Only</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="name">Name</option>
              <option value="created_at">Created Date</option>
              <option value="sort_order">Sort Order</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">View Mode</label>
            <div className="flex space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
              >
                <Grid size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
              >
                <List size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Menu Item Selection */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <h3 className="text-lg font-semibold mb-4">Select Menu Item for Mapping</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Menu</label>
            <select
              value={selectedMenu}
              onChange={(e) => setSelectedMenu(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Menus</option>
              {menus?.map(menu => (
                <option key={menu.id} value={menu.id}>{menu.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              {categories?.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Menu Item</label>
            <select
              value={selectedMenuItem?.id || ''}
              onChange={(e) => {
                const item = menuItems?.find(item => item.id === parseInt(e.target.value));
                setSelectedMenuItem(item);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Menu Item</option>
              {menuItems?.map(item => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Addon Groups Grid/List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Available Addon Groups</h3>
          <p className="text-sm text-gray-600">
            {filteredAddonGroups.length} groups found
          </p>
        </div>

        {addonGroupsLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading addon groups...</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAddonGroups.map(group => (
              <div key={group.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center space-x-2">
                    <Group className="text-blue-600" size={20} />
                    <h4 className="font-semibold text-gray-900">{group.name}</h4>
                    {group.is_required && (
                      <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Required</span>
                    )}
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleViewGroupDetails(group)}
                      className="p-1 text-green-600 hover:text-green-800"
                      title="View details and add items"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => handleAddAddonToGroup(group)}
                      className="p-1 text-purple-600 hover:text-purple-800"
                      title="Add addon items"
                    >
                      <Plus size={16} />
                    </button>
                    <button
                      onClick={() => handleMapGroup(group.id)}
                      className="p-1 text-blue-600 hover:text-blue-800"
                      title="Map to menu item"
                    >
                      <Link size={16} />
                    </button>
                    <button
                      onClick={() => {
                        setEditingGroup(group);
                        setShowCreateModal(true);
                      }}
                      className="p-1 text-gray-600 hover:text-gray-800"
                      title="Edit group"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(group.id)}
                      className="p-1 text-red-600 hover:text-red-800"
                      title="Delete group"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-3">{group.description}</p>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Quantity Range:</span>
                    <span className="font-medium">{group.min_quantity} - {group.max_quantity || '∞'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Sort Order:</span>
                    <span className="font-medium">{group.sort_order}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Addons:</span>
                    <span className="font-medium">{group.addons?.length || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Name</th>
                    <th className="text-left py-2">Description</th>
                    <th className="text-left py-2">Required</th>
                    <th className="text-left py-2">Quantity Range</th>
                    <th className="text-left py-2">Addons</th>
                    <th className="text-left py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAddonGroups.map(group => (
                    <tr key={group.id} className="border-b hover:bg-gray-50">
                      <td className="py-3">
                        <div className="flex items-center space-x-2">
                          <Group className="text-blue-600" size={16} />
                          <span className="font-medium">{group.name}</span>
                        </div>
                      </td>
                      <td className="py-3 text-sm text-gray-600">{group.description}</td>
                      <td className="py-3">
                        {group.is_required ? (
                          <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Required</span>
                        ) : (
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Optional</span>
                        )}
                      </td>
                      <td className="py-3 text-sm">{group.min_quantity} - {group.max_quantity || '∞'}</td>
                      <td className="py-3 text-sm">{group.addons?.length || 0}</td>
                      <td className="py-3">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewGroupDetails(group)}
                            className="text-green-600 hover:text-green-800"
                            title="View details and add items"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleAddAddonToGroup(group)}
                            className="text-purple-600 hover:text-purple-800"
                            title="Add addon items"
                          >
                            <Plus size={16} />
                          </button>
                          <button
                            onClick={() => handleMapGroup(group.id)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Map to menu item"
                          >
                            <Link size={16} />
                          </button>
                          <button
                            onClick={() => {
                              setEditingGroup(group);
                              setShowCreateModal(true);
                            }}
                            className="text-gray-600 hover:text-gray-800"
                            title="Edit group"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(group.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete group"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Mapped Addon Groups for Selected Menu Item */}
      {selectedMenuItem && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold">Mapped Addon Groups for "{selectedMenuItem.name}"</h3>
            <p className="text-sm text-gray-600">
              {menuItemAddonGroups?.length || 0} groups mapped
            </p>
          </div>

          {menuItemAddonGroupsLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading mapped groups...</p>
            </div>
          ) : (
            <div className="p-4">
              {menuItemAddonGroups?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {menuItemAddonGroups.map(group => (
                    <div key={group.id} className="border rounded-lg p-4 bg-blue-50">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center space-x-2">
                          <FolderOpen className="text-blue-600" size={20} />
                          <h4 className="font-semibold text-gray-900">{group.name}</h4>
                          {group.is_required && (
                            <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Required</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleUnmapGroup(group.id)}
                          className="p-1 text-red-600 hover:text-red-800"
                          title="Unmap group"
                        >
                          <Unlink size={16} />
                        </button>
                      </div>

                      <p className="text-sm text-gray-600 mb-3">{group.description}</p>

                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Quantity Range:</span>
                          <span className="font-medium">{group.min_quantity} - {group.max_quantity || '∞'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Addons:</span>
                          <span className="font-medium">{group.addons?.length || 0}</span>
                        </div>
                      </div>

                      {group.addons?.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs font-medium text-gray-500 mb-2">Available Addons:</p>
                          <div className="space-y-2">
                            {group.addons.map(addon => (
                              <div key={addon.id} className="flex items-center justify-between text-xs">
                                <div className="flex items-center space-x-2">
                                  {addon.image_url && (
                                    <img
                                      src={addon.image_url}
                                      alt={addon.name}
                                      className="w-6 h-6 object-cover rounded"
                                    />
                                  )}
                                  <span>{addon.name}</span>
                                </div>
                                <span className="font-medium">£{addon.price}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FolderPlus className="mx-auto mb-2" size={32} />
                  <p>No addon groups mapped to this menu item</p>
                  <p className="text-sm">Use the "Map" button above to add groups</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <AddonGroupModal
          group={editingGroup}
          onClose={() => {
            setShowCreateModal(false);
            setEditingGroup(null);
          }}
        />
      )}

      {showMappingModal && selectedGroup && (
        <MappingModal
          onClose={() => {
            setShowMappingModal(false);
            setSelectedGroup(null);
          }}
        />
      )}

      {/* Group Details Modal */}
      {showGroupDetailsModal && selectedGroupForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-hidden">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">
                Group Details: "{selectedGroupForDetails.name}"
              </h3>
              <button 
                onClick={() => setShowGroupDetailsModal(false)} 
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            {detailedGroupLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2">Loading group details...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Group Info */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg">Group Information</h4>
                  <div className="space-y-2">
                    <div><strong>Name:</strong> {selectedGroupForDetails.name}</div>
                    <div><strong>Description:</strong> {selectedGroupForDetails.description || 'No description'}</div>
                    <div><strong>Required:</strong> {selectedGroupForDetails.is_required ? 'Yes' : 'No'}</div>
                    <div><strong>Min Quantity:</strong> {selectedGroupForDetails.min_quantity}</div>
                    <div><strong>Max Quantity:</strong> {selectedGroupForDetails.max_quantity || 'Unlimited'}</div>
                    <div><strong>Sort Order:</strong> {selectedGroupForDetails.sort_order}</div>
                    <div><strong>Total Addons:</strong> {detailedGroupData?.addons?.length || selectedGroupForDetails.addons?.length || 0}</div>
                  </div>
                
                <button
                  onClick={() => {
                    setShowGroupDetailsModal(false);
                    setShowAddAddonModal(true);
                  }}
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700"
                >
                  ➕ Add New Addon Item
                </button>
              </div>

              {/* Existing Addons */}
              <div className="space-y-4">
                <h4 className="font-semibold text-lg">Addon Items ({detailedGroupData?.addons?.length || selectedGroupForDetails.addons?.length || 0})</h4>
                {(detailedGroupData?.addons?.length > 0 || selectedGroupForDetails.addons?.length > 0) ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {(detailedGroupData?.addons || selectedGroupForDetails.addons || []).map((addon, index) => (
                      <div key={addon.id || index} className="border rounded p-3 bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex items-start space-x-3 flex-1">
                            {addon.image_url && (
                              <div className="flex-shrink-0">
                                <img
                                  src={addon.image_url}
                                  alt={addon.name}
                                  className="w-12 h-12 object-cover rounded-md"
                                />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h5 className="font-medium">{addon.name}</h5>
                              {addon.description && (
                                <p className="text-sm text-gray-600">{addon.description}</p>
                              )}
                              <div className="text-sm text-gray-500 mt-1">
                                Price: £{addon.price} | Max: {addon.max_quantity} | Available: {addon.is_available ? 'Yes' : 'No'}
                              </div>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleEditAddon(addon)}
                              className="text-blue-600 hover:text-blue-800" 
                              title="Edit"
                            >
                              <Edit size={14} />
                            </button>
                            <button 
                              onClick={() => handleDeleteAddon(addon)}
                              className="text-red-600 hover:text-red-800" 
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="mx-auto mb-2" size={32} />
                    <p>No addon items in this group</p>
                    <p className="text-sm">Click "Add New Addon Item" to get started</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end mt-6">
            <button
              onClick={() => setShowGroupDetailsModal(false)}
              className="bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
            >
              Close
            </button>
          </div>
          </div>
        </div>
      )}

      {/* Edit Addon Items Modal */}
      {showEditAddonModal && editingAddon && selectedGroupForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-hidden">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">
                Edit Addon Item: "{editingAddon.name}"
              </h3>
              <button 
                onClick={() => {
                  setShowEditAddonModal(false);
                  setEditingAddon(null);
                  setRemoveImage(false);
                }} 
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmitEditAddon} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={editingAddon.name}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Nuts"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price * (£)
                  </label>
                  <input
                    type="number"
                    name="price"
                    step="0.01"
                    min="0"
                    defaultValue={editingAddon.price}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="1.50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  defaultValue={editingAddon.description || ''}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Roasted mixed nuts"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Addon Image
                </label>
                <FileUpload
                  onUploadSuccess={(file) => {
                    const imageUrlInput = document.querySelector('input[name="image_url"]');
                    if (imageUrlInput) {
                      imageUrlInput.value = file.url;
                    }
                  }}
                  onUploadError={(error) => {
                    console.error('Upload error:', error);
                  }}
                  multiple={false}
                  placeholder="Upload addon image"
                  showPreview={true}
                  existingImageUrl={editingAddon.image_url}
                />
                <input
                  type="hidden"
                  name="image_url"
                  defaultValue={editingAddon.image_url || ""}
                />
                
                {/* Fallback display if FileUpload doesn't show existing image */}
                {editingAddon.image_url && !removeImage && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-md border">
                    <div className="flex items-center space-x-3">
                      <img
                        src={editingAddon.image_url}
                        alt="Current addon image"
                        className="w-16 h-16 object-cover rounded-md"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.textContent = 'Image not found';
                        }}
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">Current Image</p>
                        <p className="text-xs text-gray-500">Upload a new image to replace this one</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setRemoveImage(true);
                          const imageUrlInput = document.querySelector('input[name="image_url"]');
                          if (imageUrlInput) {
                            imageUrlInput.value = '';
                          }
                        }}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
                
                {removeImage && editingAddon.image_url && (
                  <div className="mt-2 p-3 bg-red-50 rounded-md border border-red-200">
                    <div className="flex items-center space-x-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-red-900">Image will be removed</p>
                        <p className="text-xs text-red-600">Click "Cancel" to keep the current image</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setRemoveImage(false);
                          const imageUrlInput = document.querySelector('input[name="image_url"]');
                          if (imageUrlInput) {
                            imageUrlInput.value = editingAddon.image_url;
                          }
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Keep Image
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Quantity
                  </label>
                  <input
                    type="number"
                    name="min_quantity"
                    min="0"
                    defaultValue={editingAddon.min_quantity || 0}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Quantity
                  </label>
                  <input
                    type="number"
                    name="max_quantity"
                    min="1"
                    defaultValue={editingAddon.max_quantity || 3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    name="sort_order"
                    min="0"
                    defaultValue={editingAddon.sort_order || 0}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex flex-col space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Options
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_available"
                      value="true"
                      defaultChecked={editingAddon.is_available}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm">Available</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="default"
                      value="true"
                      defaultChecked={editingAddon.default}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm">Default Selection</span>
                  </label>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={updateAddonInGroupMutation.isLoading}
                >
                  {updateAddonInGroupMutation.isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Updating...
                    </div>
                  ) : (
                    'Update Addon'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditAddonModal(false);
                    setEditingAddon(null);
                    setRemoveImage(false);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Addon Items Modal */}
      {showAddAddonModal && selectedGroupForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-hidden">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">
                Add Addon Item to "{selectedGroupForDetails.name}"
              </h3>
              <button 
                onClick={() => setShowAddAddonModal(false)} 
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmitAddonToGroup} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Nuts"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price * (£)
                  </label>
                  <input
                    type="number"
                    name="price"
                    step="0.01"
                    min="0"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="1.50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Roasted mixed nuts"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Addon Image
                </label>
                <FileUpload
                  onUploadSuccess={(file) => {
                    const imageUrlInput = document.querySelector('input[name="image_url"]');
                    if (imageUrlInput) {
                      imageUrlInput.value = file.url;
                    }
                  }}
                  onUploadError={(error) => {
                    console.error('Upload error:', error);
                  }}
                  multiple={false}
                  placeholder="Upload addon image"
                  showPreview={true}
                />
                <input
                  type="hidden"
                  name="image_url"
                  defaultValue=""
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Quantity
                  </label>
                  <input
                    type="number"
                    name="min_quantity"
                    min="0"
                    defaultValue="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Quantity
                  </label>
                  <input
                    type="number"
                    name="max_quantity"
                    min="1"
                    defaultValue="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    name="sort_order"
                    min="0"
                    defaultValue="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex flex-col space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Options
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_available"
                      value="true"
                      defaultChecked
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm">Available</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="default"
                      value="true"
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm">Default Selection</span>
                  </label>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={createAddonInGroupMutation.isLoading}
                >
                  {createAddonInGroupMutation.isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Adding...
                    </div>
                  ) : (
                    'Add to Group'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddAddonModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddonGroups; 